import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpDown, faSpinner, faPlus, faBell, faCheck, faTriangleExclamation } from '@/fontawesome.ts';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import Modal from '@/components/ui/Modal.tsx';
import Checkbox from '@/components/ui/Checkbox';
import RecommendationStatsModal from '@/components/admin/recommendations/RecommendationStatsModal.tsx';
import CreateMissingSymbolsModal, { parseTickerInput } from '@/components/admin/recommendations/CreateMissingSymbolsModal.tsx';
import MultiCreateTable from '@/components/admin/recommendations/MultiCreateTable.tsx';
import type { SymbolEntry } from '@/components/admin/recommendations/CreateMissingSymbolsModal.tsx';
import { showSuccessToast, handleAPIError } from '@/utils/toasts.ts';
import { toCurrencyCentValue } from '@/utils/helpers.ts';
import { APIRoutes } from '@/services/api.routes.ts';
import type { RecommendationSwingtrade, RecommendationCreateStats, Symbol as SymbolModel } from '@/models';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import type { CreateBatchSwingTradeRequest, CreateSingleSwingTradeRequest } from '@/modelsAPI';

interface SwingTradeInput {
  id: string;
  symbolTicker: string;
  type: 'LONG' | 'SHORT';
  initialPrice: string;
  initialPercentage: string;
  takeProfit: string;
  boost: boolean;
  free: boolean;
  comment: string|null;
}

interface CreateMultipleSwingTradesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwingTradesCreated: (swingTrades: RecommendationSwingtrade[]) => void;
}

export default function CreateMultipleSwingTradesModal({
  isOpen,
  onClose,
  onSwingTradesCreated
}: CreateMultipleSwingTradesModalProps) {
  const [discordPreMessage, setDiscordPreMessage] = useState('');
  const [discordPostMessage, setDiscordPostMessage] = useState('');
  const [swingTrades, setSwingTrades] = useState<SwingTradeInput[]>([
    {
      id: Date.now().toString(),
      symbolTicker: '',
      type: 'LONG',
      initialPrice: '',
      initialPercentage: '',
      takeProfit: '',
      boost: false,
      free: false,
      comment: ''
    }
  ]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [createdStats, setCreatedStats] = useState<RecommendationCreateStats | null>(null);
  const [createdTrades, setCreatedTrades] = useState<RecommendationSwingtrade[]>([]);
  const [showActiveTradeWarning, setShowActiveTradeWarning] = useState(false);
  const [activeWarningTickers, setActiveWarningTickers] = useState<string[]>([]);

  // Symbol cache
  const symbolCacheRef = useRef<Record<string, SymbolModel | null>>({});
  const activeTradesCacheRef = useRef<Record<string, boolean>>({});
  const [symbolCacheVersion, setSymbolCacheVersion] = useState(0);

  // Missing symbols modal state
  const [showMissingSymbolsModal, setShowMissingSymbolsModal] = useState(false);
  const [symbolEntries, setSymbolEntries] = useState<SymbolEntry[]>([]);

  // Stats preview state
  const [estimatedStats, setEstimatedStats] = useState<RecommendationCreateStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [totalUserCount, setTotalUserCount] = useState<number | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleAddRow = () => {
    setSwingTrades(prevTrades => [
      ...prevTrades,
      {
        id: Date.now().toString(),
        symbolTicker: '',
        type: 'LONG',
        initialPrice: '',
        initialPercentage: '',
        takeProfit: '',
        boost: false,
        free: false,
        comment: ''
      }
    ]);
  };

  const handleRemoveRow = (id: string) => {
    setSwingTrades(prevTrades => {
      if (prevTrades.length > 1) {
        return prevTrades.filter(trade => trade.id !== id);
      }
      return prevTrades;
    });
  };

  const handleUpdateRow = (id: string, field: keyof SwingTradeInput, value: string | boolean) => {
    setSwingTrades(prevTrades => prevTrades.map(trade => {
      if (trade.id === id) {
        if (field === 'boost' || field === 'free') {
          return { ...trade, [field]: value === 'true' || value === true };
        }
        return { ...trade, [field]: value };
      }
      return trade;
    }));
  };

  const lookupSymbol = async (ticker: string, tradeId: string, retryCount = 0) => {
    const rawInput = ticker.trim().toUpperCase();
    const { ticker: parsedTicker } = parseTickerInput(rawInput);

    if (!parsedTicker || parsedTicker.length > 10 || !/^[A-Z0-9]+$/.test(parsedTicker)) {
      return;
    }

    if (symbolCacheRef.current[rawInput] !== undefined) {
      const cached = symbolCacheRef.current[rawInput];
      if (cached !== null) {
        const initialPrice = cached.currentData?.price !== null && cached.currentData?.price !== undefined
          ? cached.currentData.price.toFixed(2)
          : undefined;
        if (initialPrice !== undefined) {
          setSwingTrades(prev => prev.map(trade =>
            trade.id === tradeId ? { ...trade, initialPrice } : trade
          ));
        }
      }
      return;
    }

    const maxRetries = 1;

    try {
      setLoading(true);

      const response = await APIRoutes.listSymbols({
        ticker: parsedTicker,
        limit: 1
      });

      const exactMatch = response.symbols.find(s => s.ticker === parsedTicker);

      if (exactMatch !== undefined) {
        symbolCacheRef.current[rawInput] = exactMatch;
        const initialPrice = exactMatch.currentData?.price !== null && exactMatch.currentData?.price !== undefined
          ? exactMatch.currentData.price.toFixed(2)
          : undefined;
        if (initialPrice !== undefined) {
          setSwingTrades(prev => prev.map(trade =>
            trade.id === tradeId ? { ...trade, initialPrice } : trade
          ));
        }

        // Check for active trades with the same ticker
        try {
          const activeResponse = await APIRoutes.listSwingTrades({ ticker: parsedTicker, excludeFinished: true, limit: 1 });
          activeTradesCacheRef.current[rawInput] = activeResponse.total > 0;
        } catch {
          activeTradesCacheRef.current[rawInput] = false;
        }
      } else {
        symbolCacheRef.current[rawInput] = null;
      }
      setSymbolCacheVersion(v => v + 1);
      setLoading(false);
    } catch (err) {
      const isRetryableError = err instanceof Error &&
        (!err.message.includes('400') && !err.message.includes('404') && !err.message.includes('401'));

      console.error(`Symbol lookup failed (attempt ${retryCount + 1}/${maxRetries + 1}):`, err);

      if (retryCount < maxRetries && isRetryableError) {
        const delay = 800;
        setTimeout(() => {
          lookupSymbol(ticker, tradeId, retryCount + 1);
        }, delay);
        return;
      }

      symbolCacheRef.current[rawInput] = null;
      setSymbolCacheVersion(v => v + 1);
      setLoading(false);
    }
  };

  const collectSymbolEntries = (): SymbolEntry[] => {
    const seen = new Map<string, SymbolEntry>();
    for (const trade of swingTrades) {
      const rawInput = trade.symbolTicker.trim().toUpperCase();
      if (rawInput === '' || seen.has(rawInput)) continue;
      const { ticker, exchange } = parseTickerInput(rawInput);
      seen.set(rawInput, {
        rawInput,
        parsedTicker: ticker,
        parsedExchange: exchange,
        existingSymbol: symbolCacheRef.current[rawInput] ?? null
      });
    }
    return Array.from(seen.values());
  };

  const createRequest = (): [CreateBatchSwingTradeRequest, string[]] => {
    const payloads: CreateSingleSwingTradeRequest[] = [];
    const errors: string[] = [];

    for (const trade of swingTrades) {
      if (!trade.symbolTicker.trim()) continue;

      const tradeErrors: string[] = [];

      if (parseFloat(trade.initialPrice) <= 0) {
        tradeErrors.push(`Einstiegspreis muss größer als 0 sein`);
      }

      if (parseFloat(trade.initialPercentage) <= 0 || parseFloat(trade.initialPercentage) > 100) {
        tradeErrors.push(`Portfolio % muss zwischen 0 und 100 liegen`);
      }

      if (parseFloat(trade.takeProfit) <= 0) {
        tradeErrors.push(`Take-Profit Preis muss größer als 0 sein`);
      }

      if (tradeErrors.length > 0) {
        errors.push(`${trade.symbolTicker}: ${tradeErrors.join(', ')}`);
        continue;
      }

      const rawInput = trade.symbolTicker.trim().toUpperCase();
      const cachedSymbol = symbolCacheRef.current[rawInput];

      const payload: CreateSingleSwingTradeRequest = {
        symbolID:   cachedSymbol?.id ?? null,
        type:       trade.type,
        price:      toCurrencyCentValue(parseFloat(trade.initialPrice)),
        percentage: parseFloat(trade.initialPercentage) / 100,
        takeProfit: toCurrencyCentValue(parseFloat(trade.takeProfit)),
        boost:      trade.boost,
        free:       trade.free,
        comment:    (trade.comment?.trim() !== '' ? trade.comment?.trim() : null) ?? null,
      };

      payloads.push(payload);
    }

    const request = {
      recommendations:    payloads,
      discordPreMessage:  discordPreMessage.trim()  !== '' ? discordPreMessage.trim()  : null,
      discordPostMessage: discordPostMessage.trim() !== '' ? discordPostMessage.trim() : null,
    } satisfies CreateBatchSwingTradeRequest;

    return [request, errors];
  };

  const submitRequest = async () => {
    const [request, errors] = createRequest();

    if (errors.length > 0) {
      setError(errors.join(' | '));
      return;
    }

    if (request.recommendations.length === 0) {
      setError('Mindestens ein Trade muss ausgefüllt sein');
      return;
    }

    setLoading(true);
    try {
      const response = await APIRoutes.createSwingTrades(request, false);

      setCreatedTrades(response.recommendations);
      setCreatedStats(response.stats);
      setShowStatsModal(true);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Erstellen der Swing Trades');
    } finally {
      setLoading(false);
    }
  };

  const proceedAfterValidation = async () => {
    const allEntries = collectSymbolEntries();
    const missingSymbols = allEntries.filter(e => e.existingSymbol === null);

    if (missingSymbols.length > 0) {
      setSymbolEntries(allEntries);
      setShowMissingSymbolsModal(true);
      return;
    }

    await submitRequest();
  };

  const handleActiveTradeConfirm = async () => {
    setShowActiveTradeWarning(false);
    await proceedAfterValidation();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const [request, errors] = createRequest();

    if (errors.length > 0) {
      setError(errors.join(' | '));
      return;
    }

    if (request.recommendations.length === 0) {
      setError('Mindestens ein Trade muss ausgefüllt sein');
      return;
    }

    const activeTickers = swingTrades
      .filter(trade => {
        const raw = trade.symbolTicker.trim().toUpperCase();
        return raw !== '' && activeTradesCacheRef.current[raw] === true;
      })
      .map(trade => trade.symbolTicker.trim().toUpperCase());

    if (activeTickers.length > 0) {
      setActiveWarningTickers(activeTickers);
      setShowActiveTradeWarning(true);
      return;
    }

    await proceedAfterValidation();
  };

  const handleSymbolsReady = async (symbolMap: Record<string, SymbolModel>) => {
    for (const [rawInput, symbol] of Object.entries(symbolMap)) {
      symbolCacheRef.current[rawInput] = symbol;
    }
    setSymbolCacheVersion(v => v + 1);
    setShowMissingSymbolsModal(false);

    await submitRequest();
  };

  const isTradeValid = (trade: SwingTradeInput): boolean => {
    if (trade.symbolTicker.trim() === '') return false;

    const initialPrice = parseFloat(trade.initialPrice);
    const initialPercentage = parseFloat(trade.initialPercentage);
    const takeProfit = parseFloat(trade.takeProfit);

    if (isNaN(initialPrice) || initialPrice <= 0) return false;
    if (isNaN(initialPercentage) || initialPercentage <= 0 || initialPercentage > 100) return false;
    return !(isNaN(takeProfit) || takeProfit <= 0);
  };

  const allTradesValid = swingTrades.every(isTradeValid);

  const fetchEstimatedStats = async () => {
    if (!allTradesValid) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    const [request, errors] = createRequest();

    if (errors.length > 0) {
      setEstimatedStats(null);
      setStatsError(errors[0]);
      setStatsLoading(false);
      return;
    }

    if (request.recommendations.length === 0) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    setStatsError(null);

    try {
      const response = await APIRoutes.createSwingTrades(request, true);
      setEstimatedStats(response.stats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Statistiken';
      setStatsError(errorMessage);
      setEstimatedStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const tradesJson = JSON.stringify(swingTrades);

  useEffect(() => {
    setStatsLoading(true);

    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchEstimatedStats();
    }, 500);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [tradesJson, discordPreMessage, discordPostMessage]);

  useEffect(() => {
    if (isOpen) {
      APIRoutes.listUsers({ limit: 1 }).then(response => {
        setTotalUserCount(response.total);
      }).catch(() => {
        setTotalUserCount(null);
      });
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!loading) {
      setSwingTrades([
        {
          id: Date.now().toString(),
          symbolTicker: '',
          type: 'LONG',
          initialPrice: '',
          initialPercentage: '',
          takeProfit: '',
          boost: false,
          free: false,
          comment: ''
        }
      ]);
      setDiscordPreMessage('');
      setDiscordPostMessage('');
      setError('');
      setShowStatsModal(false);
      setCreatedStats(null);
      setCreatedTrades([]);
      setEstimatedStats(null);
      setStatsError(null);
      setShowMissingSymbolsModal(false);
      setSymbolEntries([]);
      setShowActiveTradeWarning(false);
      setActiveWarningTickers([]);
      symbolCacheRef.current = {};
      activeTradesCacheRef.current = {};
      setSymbolCacheVersion(0);
      onClose();
    }
  };

  const handleStatsModalClose = () => {
    onSwingTradesCreated(createdTrades);
    showSuccessToast(`${createdTrades.length} Swing Trade(s) erfolgreich erstellt`);
    handleClose();
  };

  void symbolCacheVersion;

  return (
    <>
    <Modal
      isOpen={isOpen && !showStatsModal && !showMissingSymbolsModal && !showActiveTradeWarning}
      onClose={handleClose}
      title="Mehrere Swing Trades erstellen"
      icon={<FontAwesomeIcon icon={faUpDown} className="w-5 h-5 text-purple-600" />}
      maxWidth="screen"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-4 sm:p-6">
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Discord Pre Message */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Discord Nachricht (vor allen Tradingideen)
          </label>
          <textarea
            value={discordPreMessage}
            onChange={(e) => setDiscordPreMessage(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Optional: Nachricht, die vor allen Swing Trades gesendet wird"
            disabled={loading}
          />
        </div>

        <MultiCreateTable
          rows={swingTrades}
          keyExtractor={(trade) => trade.id}
          onRemove={(trade) => handleRemoveRow(trade.id)}
          removeDisabled={loading}
          rowLabel={(index) => `Trade ${index + 1}`}
          fields={[
            {
              key: 'ticker',
              header: 'Ticker *',
              primary: true,
              render: (trade) => {
                const rawInput = trade.symbolTicker.trim().toUpperCase();
                const symbolFound = rawInput !== '' && symbolCacheRef.current[rawInput] !== undefined && symbolCacheRef.current[rawInput] !== null;
                return (
                  <div className="relative w-full md:w-28">
                    <input
                      type="text"
                      value={trade.symbolTicker}
                      onChange={(e) => handleUpdateRow(trade.id, 'symbolTicker', e.target.value.toUpperCase())}
                      onBlur={(e) => lookupSymbol(e.target.value, trade.id)}
                      className={`w-full md:w-28 px-2 py-1 pr-7 text-sm rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                        activeTradesCacheRef.current[rawInput] === true
                          ? 'border-2 border-amber-500 dark:border-amber-400 focus:ring-amber-500 focus:border-amber-500'
                          : 'border border-gray-300 dark:border-gray-600 focus:ring-purple-500 focus:border-purple-500'
                      }`}
                      title={activeTradesCacheRef.current[rawInput] === true ? 'Aktiver Trade' : undefined}
                      placeholder="AAPL"
                      disabled={loading}
                    />
                    {symbolFound && (
                      <FontAwesomeIcon icon={faCheck} className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-green-500" />
                    )}
                  </div>
                );
              }
            },
            {
              key: 'type',
              header: 'Richtung *',
              render: (trade) => (
                <select
                  value={trade.type}
                  onChange={(e) => handleUpdateRow(trade.id, 'type', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
                  disabled={loading}
                >
                  <option value="LONG">LONG</option>
                  <option value="SHORT">SHORT</option>
                </select>
              )
            },
            {
              key: 'initialPrice',
              header: 'Einstiegspreis *',
              render: (trade) => (
                <input
                  type="number"
                  value={trade.initialPrice}
                  onChange={(e) => handleUpdateRow(trade.id, 'initialPrice', e.target.value)}
                  step="0.01"
                  min="0"
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="100.00"
                  disabled={loading}
                />
              )
            },
            {
              key: 'initialPercentage',
              header: 'Position (%) *',
              render: (trade) => (
                <input
                  type="number"
                  value={trade.initialPercentage}
                  onChange={(e) => handleUpdateRow(trade.id, 'initialPercentage', e.target.value)}
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="25.00"
                  disabled={loading}
                />
              )
            },
            {
              key: 'takeProfit',
              header: 'Take-Profit *',
              render: (trade) => (
                <input
                  type="number"
                  value={trade.takeProfit}
                  onChange={(e) => handleUpdateRow(trade.id, 'takeProfit', e.target.value)}
                  step="0.01"
                  min="0"
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="120.00"
                  disabled={loading}
                />
              )
            },
            {
              key: 'boost',
              header: 'Boost',
              align: 'center',
              mobileInline: true,
              render: (trade) => (
                <Checkbox
                  checked={trade.boost}
                  onChange={(checked) => handleUpdateRow(trade.id, 'boost', checked.toString())}
                  colorTheme="purple"
                />
              )
            },
            {
              key: 'free',
              header: 'Free',
              align: 'center',
              mobileInline: true,
              render: (trade) => (
                <Checkbox
                  checked={trade.free}
                  onChange={(checked) => handleUpdateRow(trade.id, 'free', checked.toString())}
                  colorTheme="purple"
                />
              )
            },
            {
              key: 'comment',
              header: 'Kommentar',
              render: (trade) => (
                <input
                  type="text"
                  value={trade.comment ?? ''}
                  onChange={(e) => handleUpdateRow(trade.id, 'comment', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Optional"
                  disabled={loading}
                />
              )
            }
          ]}
        />

        <div className="mt-4">
          <button
            type="button"
            onClick={handleAddRow}
            disabled={loading}
            className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 text-sm flex items-center text-left cursor-pointer disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-1" />
            Weitere Zeile hinzufügen
          </button>
        </div>

        {/* Discord Post Message */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Discord Nachricht (nach allen Tradingideen)
          </label>
          <textarea
            value={discordPostMessage}
            onChange={(e) => setDiscordPostMessage(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Optional: Nachricht, die nach allen Swing Trades gesendet wird"
            disabled={loading}
          />
        </div>

        {/* Notification Options */}
        <label className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 mt-6">
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <FontAwesomeIcon icon={faBell} className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Benachrichtigungen</span>
              </div>
              {statsLoading && (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-gray-400" />
              )}
            </div>

            {statsError !== null && (
              <p className="text-xs text-red-500 dark:text-red-400 mt-2">{statsError}</p>
            )}

            <div className="mt-2 space-y-1">
              {estimatedStats !== null ? (
                <>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.discordMessages === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faDiscord} className="w-3 h-3 mr-2" />
                    Discord-Nachrichten: {estimatedStats.discordMessages}
                  </p>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.telegramMessages === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faTelegram} className="w-3 h-3 mr-2" />
                    Telegram-Nachrichten: {estimatedStats.telegramMessages}
                  </p>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.scnNotifications === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
                    SCN Push-Nachrichten: {estimatedStats.scnNotifications}
                  </p>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.pushNotifications === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
                    Push (App): {estimatedStats.pushNotifications}
                  </p>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.mailNotifications === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 mr-2" />
                    E-Mails an {estimatedStats.mailNotifications}{totalUserCount !== null ? ` von insgesamt ${totalUserCount} Nutzern` : ''}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faDiscord} className="w-3 h-3 mr-2" />
                    Sendet Discord-Nachrichten in die entsprechenden Channel
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faTelegram} className="w-3 h-3 mr-2" />
                    Sendet Telegram-Nachrichten
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
                    Sendet SCN Push-Benachrichtigungen an Nutzer, welche das konfiguriert haben
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
                    Sendet Push-Benachrichtigungen (App) an Nutzer, welche diese aktiviert haben
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 mr-2" />
                    Sendet E-Mails an Nutzer
                  </p>
                </>
              )}
            </div>
          </div>
        </label>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !allTradesValid}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Erstelle...
              </>
            ) : (
              'Swing Trades erstellen'
            )}
          </button>
        </div>
      </form>
    </Modal>

    <Modal
      isOpen={showActiveTradeWarning}
      onClose={() => setShowActiveTradeWarning(false)}
      title="Aktive Trades vorhanden"
      icon={<FontAwesomeIcon icon={faTriangleExclamation} className="w-5 h-5 text-amber-500" />}
      maxWidth="md"
    >
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Für folgende Ticker existieren bereits aktive Trades:
        </p>
        <ul className="list-disc list-inside space-y-1">
          {activeWarningTickers.map(ticker => (
            <li key={ticker} className="text-sm font-medium text-amber-600 dark:text-amber-400">
              {ticker}
            </li>
          ))}
        </ul>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Möchtest du trotzdem fortfahren?
        </p>
        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={() => setShowActiveTradeWarning(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
          >
            Zurück
          </button>
          <button
            type="button"
            onClick={handleActiveTradeConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-md hover:bg-amber-700 cursor-pointer"
          >
            Trotzdem erstellen
          </button>
        </div>
      </div>
    </Modal>

    <CreateMissingSymbolsModal
      isOpen={showMissingSymbolsModal}
      onClose={() => setShowMissingSymbolsModal(false)}
      symbols={symbolEntries}
      onSymbolsReady={handleSymbolsReady}
    />

    {createdStats !== null && (
      <RecommendationStatsModal
        isOpen={showStatsModal}
        onClose={handleStatsModalClose}
        stats={createdStats}
        recommendationType="swing"
        text={`${createdTrades.length} SwingTrade-Empfehlung${createdTrades.length > 1 ? 'en' : ''} erfolgreich erstellt`}
      />
    )}
    </>
  );
}
